// HEAL-43 — `/profile/delete`: удаление аккаунта пользователем.
//
// Отдельный экран, а не диалог поверх Профиля: перечень того, что исчезнет, в
// модалку не помещается, а удаление медицинских документов — не то действие, где
// уместно «ой, промахнулся по кнопке». Два барьера: осознанный переход на этот
// экран и ввод пароля.
//
// После успеха уводим на вход и чистим всё локальное: кэш запросов, состояние
// навигации по маркерам и токен. Иначе следующий пользователь на этом устройстве
// увидит в кэше чужие анализы.

import { useState } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { AlertTriangle } from 'lucide-react';

import { useDeleteAccount } from '@/api/hooks/profile';
import { applyValidationErrors, useApiErrorToast } from '@/api/error';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { FormField } from '@/components/forms/FormField';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { clearToken } from '@/lib/auth-storage';
import { clearMarkerNavState } from '@/lib/markerNavState';

const WHAT_GETS_DELETED = [
  'профиль и анкета здоровья',
  'все загруженные анализы и их файлы',
  'история показателей и графики',
  'записи о визитах к врачу и вложения',
  'рекомендации и настройки',
];

type FormValues = { password: string };

export function DeleteAccount() {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const showErrorToast = useApiErrorToast();
  const deleteMut = useDeleteAccount();

  // Второе подтверждение: до него форму с паролем даже не показываем.
  const [confirmed, setConfirmed] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { password: '' } });

  const onSubmit = handleSubmit(async (form) => {
    if (deleteMut.isPending) return; // повторный тап = второй DELETE в полёте
    try {
      await deleteMut.mutateAsync({ password: form.password });
      qc.clear();
      clearMarkerNavState();
      await clearToken();
      toast({
        title: 'Аккаунт удалён',
        description: 'Данные и файлы удалены безвозвратно.',
        variant: 'success',
      });
      setLocation('/signin');
    } catch (err) {
      // Неверный пароль приходит как 422 на поле password — показываем под ним,
      // а не общим тостом «что-то пошло не так».
      const applied = applyValidationErrors<keyof FormValues>(err, setError);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <ScreenHeader title="Удаление аккаунта" onBack={() => setLocation('/profile')} />

      <main className="flex flex-1 flex-col overflow-y-auto px-5 pb-6 pt-2">
        <div className="mb-4 rounded-surface border border-red-200 bg-red-50 p-4">
          <div className="mb-2 flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" aria-hidden />
            <span className="text-sm font-semibold">Действие необратимо</span>
          </div>
          <p className="text-hl-secondary leading-relaxed text-red-900">
            Восстановить аккаунт и данные после удаления невозможно — ни вами, ни поддержкой.
            Войти под этой почтой снова не получится.
          </p>
        </div>

        <div className="mb-2 text-hl-small font-bold uppercase tracking-wider text-muted-foreground">
          Что будет удалено
        </div>
        <ul className="mb-6 space-y-1.5">
          {WHAT_GETS_DELETED.map((item) => (
            <li key={item} className="flex gap-2 text-hl-secondary leading-snug text-gray-700">
              <span aria-hidden className="text-gray-400">
                •
              </span>
              {item}
            </li>
          ))}
        </ul>

        {!confirmed ? (
          <div className="mt-auto space-y-2">
            <Button
              type="button"
              onClick={() => setConfirmed(true)}
              className="w-full bg-red-600 text-white hover:bg-red-700"
            >
              Я понимаю, удалить аккаунт
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLocation('/profile')}
              className="w-full"
            >
              Отмена
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-auto space-y-4" noValidate>
            <FormField
              label="Пароль"
              type="password"
              autoComplete="current-password"
              placeholder="Введите пароль"
              hint="Подтвердите, что аккаунт удаляете именно вы."
              error={errors.password?.message}
              {...register('password', { required: 'Введите пароль' })}
            />
            <Button
              type="submit"
              disabled={deleteMut.isPending}
              className="w-full bg-red-600 text-white hover:bg-red-700"
            >
              {deleteMut.isPending ? 'Удаляем…' : 'Удалить аккаунт навсегда'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={deleteMut.isPending}
              onClick={() => setLocation('/profile')}
              className="w-full"
            >
              Отмена
            </Button>
          </form>
        )}
      </main>
    </MobileFrame>
  );
}
